<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px; font-size: small;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #FFF8F0;">
          <h5 class="card-title">Gestión de Ítems</h5>
          <div class="row mb-3">
            <div class="col-md-3">
              <div class="form-group">
                <select 
                  v-model="filters.id_categoria" 
                  class="form-select" 
                  @change="handleSearch"
                >
                  <option value="">Todas las categorías</option>
                  <option 
                    v-for="categoria in categorias" 
                    :key="categoria.id" 
                    :value="categoria.id"
                  >
                    {{ categoria.nombre_categoria }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="input-group">
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control"
                    placeholder="Buscar por nombre o descripción..."
                    @keyup.enter="handleSearch"
                  />
                  <button 
                    class="btn btn-outline-secondary" 
                    type="button" 
                    @click="handleSearch"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div class="col-md-3">
              <BButton
                v-if="hasPermission('param_create')"
                @click="showModal(null)"
                variant="primary"
                class="btn-round me-2"
              >
                <i class="fa fa-plus me-1"></i> Nuevo Ítem
              </BButton>
              <BButton
                v-if="hasPermission('param_create')"
                variant="danger"
                class="btn-round"
                @click="generatePDF"
              >
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>
          
          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>Codigo</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Categoría</th>
                    <th>UndMedida</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td>{{ item.codigo }}</td>
                    <td>{{ item.nombre_item }}</td>
                    <td>{{ item.descripcion }}</td>
                    <td>{{ item.categoria?.nombre_categoria }} ({{ item.categoria?.id_partida }})</td>
                    <td>{{ item.unidad_medida }}</td>
                    <td>
                      <div class="btn-group">
                        <BButton
                          v-if="hasPermission('param_update')"
                          @click="showModal(item)"
                          variant="warning"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-edit"></i>
                        </BButton>
                        <BButton
                          v-if="hasPermission('param_delete')"
                          @click="confirmDelete(item)"
                          variant="danger"
                          size="sm"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Paginación -->
            <div class="row mt-3" v-if="pagination.total > 0">
              <div class="col-md-12">
                <div class="d-flex justify-content-between align-items-center">
                  <small class="text-muted">
                    Mostrando {{ showingFrom }} a {{ showingTo }} de {{ pagination.total }} registros
                  </small>
                  <BPagination
                    v-model="pagination.current_page"
                    :total-rows="pagination.total"
                    :per-page="pagination.per_page"
                    @change="handlePageChange"
                    size="sm"
                  ></BPagination>
                </div>
              </div>
            </div>

            <!-- Estado vacío -->
            <div v-if="!loading && items.length === 0" class="text-center py-4">
              <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
              <p class="text-muted">No se encontraron ítems</p>
              <BButton
                v-if="hasPermission('param_create')"
                @click="showModal(null)"
                variant="primary"
                class="btn-round"
              >
                <i class="fa fa-plus me-1"></i> Crear primer ítem
              </BButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar -->
    <BModal
      v-model="modalVisible"
      :title="modalTitle"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-2">
            <div class="form-group">
              <label class="form-label">codigo</label>
              <input
                type="text"
                v-model="form.codigo"
                class="form-control"
                :class="{ 'is-invalid': errors.codigo }"
                required
                maxlength="15"
              />
              <div class="invalid-feedback" v-if="errors.codigo">
                {{ errors.codigo[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-10">
            <div class="form-group">
              <label class="form-label">Nombre del Ítem</label>
              <input
                type="text"
                v-model="form.nombre_item"
                class="form-control"
                :class="{ 'is-invalid': errors.nombre_item }"
                required
                maxlength="100"
              />
              <div class="invalid-feedback" v-if="errors.nombre_item">
                {{ errors.nombre_item[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Categoría</label>
              <select
                v-model="form.id_categoria"
                class="form-select"
                :class="{ 'is-invalid': errors.id_categoria }"
                required
              >
                <option value="">--Seleccione una categoría--</option>
                <option
                  v-for="categoria in categorias"
                  :key="categoria.id"
                  :value="categoria.id"
                >
                  {{ categoria.nombre_categoria }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.id_categoria">
                {{ errors.id_categoria[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Unidad de Medida</label>
              <select
                v-model="form.unidad_medida"
                class="form-select"
                :class="{ 'is-invalid': errors.unidad_medida }"
                required
              >
                <option value="">--Seleccione una unidad--</option>
                <option
                  v-for="unidad in unidades"
                  :key="unidad.unidas"
                  :value="unidad.unidad"
                >
                  {{ unidad.unidad }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.unidad_medida">
                {{ errors.unidad_medida[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Descripción</label>
              <textarea
                v-model="form.descripcion"
                class="form-control"
                :class="{ 'is-invalid': errors.descripcion }"
                rows="3"
              ></textarea>
              <div class="invalid-feedback" v-if="errors.descripcion">
                {{ errors.descripcion[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer mt-3">
          <BButton
            type="button"
            variant="secondary"
            @click="modalVisible = false"
            class="me-2"
          >
            Cancelar
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="loadingForm"
          >
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import Swal from 'sweetalert2';
import { BModal, BButton, BPagination } from 'bootstrap-vue-3';
import { watch } from 'vue';

interface Item {
  id: number;
  codigo: string;
  nombre_item: string;
  descripcion: string | null;
  unidad_medida: string;
  id_categoria: number;
  categoria?: {
    nombre_categoria: string;
  };
  unidad_medida_obj?: {
    unidad: string;
  };
}

interface Categoria {
  id: number;
  nombre_categoria: string;
}

interface UnidadMedida {
  id: number;
  unidad: string;
}

interface Filters {
  search: string;
  id_categoria: string;
}

interface FormData {
  id: number | null;
  codigo: string;
  nombre_item: string;
  descripcion: string;
  unidad_medida: string;
  id_categoria: number | null;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export default {
  components: {
    BModal,
    BButton,
    BPagination
  },
  setup() {
    const items = ref<Item[]>([]);
    const categorias = ref<Categoria[]>([]);
    const unidades = ref<UnidadMedida[]>([]);
    const loading = ref(true);
    const authStore = useAuthStore();
    const filters = ref<Filters>({
      search: '',
      id_categoria: ''
    });
    
    // Variables para el modal
    const modalVisible = ref(false);
    const isEditing = ref(false);
    const loadingForm = ref(false);
    const errors = ref<Record<string, string[]>>({});

    const form = ref<FormData>({
      id: null,
      codigo: '',
      nombre_item: '',
      descripcion: '',
      unidad_medida: '',
      id_categoria: ''
    });

    // Paginación
    const pagination = ref<Pagination>({
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1
    });

    const modalTitle = computed(() => {
      return isEditing.value ? 'Editar Ítem' : 'Nuevo Ítem';
    });

    const showingFrom = computed(() => {
      return ((pagination.value.current_page - 1) * pagination.value.per_page) + 1;
    });

    const showingTo = computed(() => {
      const to = pagination.value.current_page * pagination.value.per_page;
      return to > pagination.value.total ? pagination.value.total : to;
    });
    const showModal = (item: Item | null) => {
      errors.value = {};
      if (item) {
        // Edición
        isEditing.value = true;
        form.value = {
          id: item.id,
          codigo: item.codigo,
          nombre_item: item.nombre_item,
          descripcion: item.descripcion || '',
          unidad_medida: item.unidad_medida,
          id_categoria: item.id_categoria
        };
      } else {
        // Nuevo
        isEditing.value = false;
        form.value = {
          id: null,
          nombre_item: '',
          descripcion: '',
          unidad_medida: '',
          id_categoria: ''
        };
      }
      modalVisible.value = true;
    };

    const handleSubmit = async () => {
      loadingForm.value = true;
      errors.value = {};
      
      try {
        let response;
        
        if (isEditing.value && form.value.id) {
          response = await axios.post(`/item/${form.value.id}/update`, form.value);
        } else {
          response = await axios.post('/item/save', form.value);
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: isEditing.value ? 'Ítem actualizado correctamente' : 'Ítem creado correctamente'
        });
        
        modalVisible.value = false;
        fetchItems();
      } catch (error: any) {
        if (error.response && error.response.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Ocurrió un error al procesar la solicitud'
          });
        }
      } finally {
        loadingForm.value = false;
      }
    };
    const fetchItems = async () => {
      loading.value = true;
      try {
        const params: Record<string, any> = {
          page: pagination.value.current_page,
          per_page: pagination.value.per_page
        };
        
        if (filters.value.search) {
          params.search = filters.value.search;
        }
        
        if (filters.value.id_categoria) {
          params.id_categoria = filters.value.id_categoria;
        }
        
        const response = await axios.get('/item/listar', { params });
        items.value = response.data.items.data || [];
        
        // Actualizar paginación con valores por defecto si no existen
        const itemsData = response.data.items;
        pagination.value = {
          current_page: itemsData.current_page || 1,
          per_page: itemsData.per_page || 10,
          total: itemsData.total || 0,
          last_page: itemsData.last_page || 1
        };
        
      } catch (error) {
        console.error('Error al cargar ítems:', error);
        items.value = [];
        pagination.value.total = 0;
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los ítems'
        });
      } finally {
        loading.value = false;
      }
    };
    /*
    const fetchItems = async () => {
      loading.value = true;
      try {
        const params: Record<string, any> = {
          page: pagination.value.current_page,
          per_page: pagination.value.per_page
        };
        
        if (filters.value.search) {
          params.search = filters.value.search;
        }
        
        if (filters.value.id_categoria) {
          params.id_categoria = filters.value.id_categoria;
        }
        
        const response = await axios.get('/item/listar', { params });
        items.value = response.data.items.data;
        
        // Actualizar paginación
        pagination.value = {
          current_page: response.data.items.current_page,
          per_page: response.data.items.per_page,
          total: response.data.items.total,
          last_page: response.data.items.last_page
        };
        
      } catch (error) {
        console.error('Error al cargar ítems:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los ítems'
        });
      } finally {
        loading.value = false;
      }
    };
    */
    const fetchCategorias = async () => {
      try {
        const response = await axios.get('/item/categorias');
        categorias.value = response.data.categorias;
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      }
    };

    const fetchUnidadesMedida = async () => {
      try {
        const response = await axios.get('/item/unidades-medida');
        unidades.value = response.data.unidades;
      } catch (error) {
        console.error('Error al cargar unidades de medida:', error);
      }
    };

    const handleSearch = () => {
      pagination.value.current_page = 1;
      fetchItems();
    };
    watch(() => pagination.value.current_page, (newPage, oldPage) => {
      if (newPage !== oldPage) {
        console.log('Página cambiada vía watch:', newPage);
        fetchItems();
      }
    });
    const handlePageChange = (page: number) => {
      console.log('Cambiando a página:', page); // Debug
      pagination.value.current_page = page;
      fetchItems();
    };
    
    const confirmDelete = (item: Item) => {
      Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar el ítem "${item.nombre_item}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteItem(item.id);
        }
      });
    };
    
    const deleteItem = async (id: number) => {
      try {
        await axios.get(`/item/${id}/delete`);
        Swal.fire({
          icon: 'success',
          title: 'Ítem eliminado',
          text: 'El ítem ha sido eliminado correctamente'
        });
        await fetchItems();
      } catch (error: any) {
        console.error('Error al eliminar ítem:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar el ítem'
        });
      }
    };

    const generatePDF = () => {
      const params = {
        search: filters.value.search,
        id_categoria: filters.value.id_categoria
      };
      
      axios.get('/item/reporte', {
        params,
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('stream', `reporte_items.pdf`);
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }).catch(error => {
        console.error('Error al generar PDF:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF'
        });
      });
    };
    
    const hasPermission = (permission: string) => {
      return authStore.hasPermission(permission);
    };
    
    onMounted(() => {
      fetchItems();
      fetchCategorias();
      fetchUnidadesMedida();
    });
    
    return {
      items,
      categorias,
      unidades,
      filters,
      loading,
      form,
      errors,
      loadingForm,
      isEditing,
      modalTitle,
      modalVisible,
      pagination,
      showingFrom,  // ← Agregar esto
      showingTo,    // ← Agregar esto
      handleSearch,
      handlePageChange,
      confirmDelete,
      hasPermission,
      showModal,
      handleSubmit,
      generatePDF
    };
  }
};
</script>

<style scoped>
  thead th {
    background-color: #D84315;
    color:#ffffff;
    position: sticky;
    top: 0;
  }
  tbody td {
    margin: 0px;
    padding: 8px !important;
    vertical-align: middle;
  }
  .table-responsive {
    max-height: 600px;
    overflow-y: auto;
  }
  .btn-round {
    border-radius: 50px;
  }
</style>